/**
 * @file 用于引导 100、 107 用户做实名认证的弹窗
 * @author edwardzhao、alfielv、malcolmmzma
 */

import _ from 'lodash';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Dialog, Viewer, Button } from '@tencent/spaui';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';

import globalStore from '@/stores';
import { hasFeatureFlag } from '@/services';
import {
  getUser,
  newRealNameQRCode,
  getUserCert,
} from '@/api/operator/cgi/agm/index';
import withFeatureFlag from '@/common/with-feature-flag';
import { handleErrResponse } from '@/pages/agency/utils/util';

import { superAdminContent, adminContent } from './constants';

import './index.less';

const ROLE_CONTENT_MAP = {
  100: superAdminContent,
  117: adminContent,
};

enum EnumApprovalStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  EMPTY = 'EMPTY',
}

interface IUserCertInfo {
  role: number;
  certList: any[];
  approvalStatus: EnumApprovalStatus;
}

// 未实名认证弹窗
const NotRealNameVerifyDialog = (props: {
  userId: number;
  onClose: () => void;
}) => {
  const { userId, onClose } = props;

  const [realNameQR, setRealNameQR] = useState('');

  useEffect(() => {
    // 扫码认证后到认证状态生效（/user/get获取）需要处理时间。
    // 此时如果再次调用newRealNameQRCode接口，会返回30002错误码来表示“已完成实名认证”。
    // 故手动处理此错误码，不再展示全局错误弹窗。
    newRealNameQRCode(
      {
        userId,
        authType: 'WX_PAY',
        validPeriod: 60 * 60 * 48,
      },
      false
    ).then((rsp: any) => {
      const { code, data } = rsp || {};
      if (code === 0) {
        setRealNameQR(data?.qr_code);
      } else if (code === 30002) {
        setRealNameQR('');
      } else {
        handleErrResponse(rsp);
      }
    });
  }, [userId]);

  return realNameQR ? (
    <Dialog
      title="员工实名认证"
      size="small"
      onClose={onClose}
      onSubmit={() => window.location.reload()}
    >
      <div className="real-name-guide-dialog">
        <img src={`data:image/png;base64,${realNameQR}`} className="qr-img" />
        <span className="tips">请使用微信扫码完成员工实名认证</span>
      </div>
    </Dialog>
  ) : null;
};

// 员工资质未审核通过弹窗
const EmployeeNotApprovalDialog = (props: {
  onClose: () => void;
  userCertInfo: IUserCertInfo;
}) => {
  const { onClose, userCertInfo } = props;
  const { role, approvalStatus } = userCertInfo;

  const [imageShow, setImageShow] = useState(false);

  // 根据role id获取对应的弹窗内容
  const dialogContent = _.get(
    _.get(ROLE_CONTENT_MAP, role, {}),
    'verified.cert',
    null
  );

  if (!dialogContent) {
    return null;
  }

  const message = useMemo(() => {
    // user在当前的bm下不存在注册资质记录
    if (approvalStatus === EnumApprovalStatus.EMPTY) {
      return dialogContent.empty;
    }
    if (role === 100) {
      if (approvalStatus === EnumApprovalStatus.REJECTED) {
        return dialogContent.fulfilled.anyRejected;
      }
      if (approvalStatus === EnumApprovalStatus.PENDING) {
        return dialogContent.fulfilled.pending;
      }
    }
    return null;
  }, []);

  // 100已实名，但员工在所属公司下未审核通过（包括无记录）；或117已实名，但不存在注册资质纪录
  return message ? (
    <Dialog
      className="not-real-name-verify-dialog"
      title="员工企业身份认证"
      onClose={onClose}
      cancelButton={false}
      submitLabel="我知道了"
    >
      <div className="not-real-name-verify-dialog-body">
        <p>{message.content}</p>
        {message.imageUrl && (
          <div>
            <Button displayType="link" onClick={() => setImageShow(true)}>
              点击查看操作引导
            </Button>
            <Viewer
              show={imageShow}
              onClose={() => setImageShow(false)}
              data={[{ name: '操作引导', url: message.imageUrl }]}
              index={0}
              type="images"
              download={true}
              footShow={false}
              dragImage={true}
            />
          </div>
        )}
      </div>
    </Dialog>
  ) : null;
};

const RealNameGuideDialog: React.FC<any> = observer((props) => {
  const userId = globalStore.account?.user_id;
  // 关闭dialog
  const [closed, setClosed] = useState(false);
  // 是否实名认证过
  const [hasRealNameVerify, setHasRealNameVerify] = useState<null | boolean>(
    null
  );
  // 员工在所属公司下是否全部审核通过
  const [userCertInfo, setUserCertInfo] = useState<IUserCertInfo | null>(null);

  // dialog是否可见
  const mayShow =
    !closed &&
    Boolean(userId) &&
    hasFeatureFlag('is_enable_strict_employee_rule') &&
    Boolean(
      globalStore.account?.role_list?.['100'] ||
        globalStore.account?.role_list?.['117']
    );

  useEffect(() => {
    getUser(userId).then((res) => {
      const { code, data } = res || {};
      if (code === 0) {
        setHasRealNameVerify(data?.real_name_verify_status === 1);
      }
    });

    getUserCert(userId).then((res) => {
      const { code, data } = res || {};
      const { list } = data || {};
      if (code === 0) {
        const roleId = (() => {
          const roleList = _.keys(globalStore.account?.role_list);
          if (roleList.includes('100')) {
            return 100;
          }
          if (roleList.includes('117')) {
            return 117;
          }
          return 0;
        })();
        let approvalStatus = EnumApprovalStatus.EMPTY;
        if (_.isEmpty(list)) {
          setUserCertInfo({
            role: roleId,
            certList: [],
            approvalStatus,
          });
          return;
        }
        if (_.some(list, (item) => item?.approval_status === 2)) {
          // 员工在所有所属公司下任一审核拒绝
          approvalStatus = EnumApprovalStatus.REJECTED;
        } else if (_.every(list, (item) => item?.approval_status === 1)) {
          // 员工在所有所属公司下都审核通过
          approvalStatus = EnumApprovalStatus.APPROVED;
        } else if (_.every(list, (item) => item?.approval_status === 0)) {
          // 员工在所有所属公司下都待审核
          approvalStatus = EnumApprovalStatus.PENDING;
        }
        setUserCertInfo({
          role: roleId,
          certList: list,
          approvalStatus,
        });
      }
    });
  }, [userId]);

  const onClose = useCallback(() => setClosed(true), []);

  const { pathname } = useLocation();
  const isActivePath = useMemo(
    () => pathname === '/organization/employee' || pathname === '/home',
    [pathname]
  );

  // 使用effect监听路由变化，重置dialog状态
  useEffect(() => {
    setClosed(false);
  }, [pathname]);

  // 非指定路径下，不展示
  if (!isActivePath) {
    return null;
  }

  // 不满足逻辑前置条件，或未获取到实名接口返回
  if (!mayShow || _.isNil(hasRealNameVerify) || _.isNil(userCertInfo)) {
    return null;
  }

  // 未实名
  if (hasRealNameVerify === false) {
    return <NotRealNameVerifyDialog userId={userId} onClose={onClose} />;
  }

  // TODO: 暂时kill掉所有审核未通过弹窗弹窗
  // 员工在所属公司下未审核通过（包括无记录）
  // if (userCertInfo.approvalStatus !== EnumApprovalStatus.APPROVED) {
  //   return <EmployeeNotApprovalDialog onClose={onClose} userCertInfo={userCertInfo} />;
  // }

  return <></>;
});

export default React.memo(withFeatureFlag(RealNameGuideDialog));
